/**
 * BuildLog — monospace log viewer with color-coded lines and auto-scroll.
 */

import { useEffect, useRef } from 'react';

interface BuildLogProps {
  /** Raw log text, one entry per line. */
  logs: string;
  /** Whether to auto-scroll to the bottom when content changes. */
  autoScroll?: boolean;
}

type LogLevel = 'info' | 'success' | 'warn' | 'error';

/** Detect the log level of a line based on common prefixes. */
const detectLevel = (line: string): LogLevel => {
  const lower = line.toLowerCase();
  if (lower.includes('[error]') || lower.includes('error:') || lower.startsWith('error')) {
    return 'error';
  }
  if (lower.includes('[warn]') || lower.includes('warning:') || lower.startsWith('warn')) {
    return 'warn';
  }
  if (lower.includes('[success]') || lower.includes('complete') || lower.includes('done')) {
    return 'success';
  }
  return 'info';
};

export const BuildLog = ({ logs, autoScroll = true }: BuildLogProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoScroll && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  const lines = logs.split('\n');

  return (
    <div
      className="build-log"
      ref={containerRef}
      role="log"
      aria-label="Build log output"
      aria-live="polite"
    >
      {lines.map((line, i) => {
        const level = detectLevel(line);
        return (
          <span key={i} className={`build-log__line build-log__line--${level}`}>
            {line}
            {'\n'}
          </span>
        );
      })}
    </div>
  );
};
